// frontend/src/pages/SRPConfig.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/UIComponents";

export default function SRPConfig() {
  const nav = useNavigate();

  // ---------- Left: search + list ----------
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [errorList, setErrorList] = useState("");

  // ---------- Right: form ----------
  const empty = {
    hd_exw_id: null,
    opt_name: "",
    exw: 0,
    price_group_id: 5,
    prod_line_id: null,
  };
  const [form, setForm] = useState(empty);
  const [initialForm, setInitialForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [errorSave, setErrorSave] = useState("");
  const [hint, setHint] = useState("");

  // ---------- Responsive ----------
  const [isNarrow, setIsNarrow] = useState(() =>
    typeof window === "undefined" ? false : window.innerWidth <= 920
  );
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth <= 920);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const qRef = useRef(null);

  // Debounced manual search
  useEffect(() => {
    const t = setTimeout(() => loadList(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  // Initial load (shows up to 500 rows)
  useEffect(() => {
    loadList("");
    setTimeout(() => qRef.current && qRef.current.focus(), 0);
  }, []);

  async function loadList(term) {
    try {
      setLoadingList(true);
      setErrorList("");
      const u = new URL("/api/data", window.location.origin);
      u.searchParams.set("scope", "srp_config");
      if (term && term.trim()) u.searchParams.set("q", term.trim());

      const r = await fetch(u.toString(), { method: "GET" });
      const j = await r.json();
      if (!r.ok || !j.ok) throw new Error(j?.error || "HTTP " + r.status);
      setRows(Array.isArray(j.rows) ? j.rows : []);
    } catch (e) {
      setErrorList(String(e.message || e));
    } finally {
      setLoadingList(false);
    }
  }

  function selectRow(r) {
    const selected = {
      hd_exw_id: r.hd_exw_id,
      opt_name: r.opt_name || "",
      exw: num(r.exw),
      price_group_id: r.price_group_id ?? 5,
      prod_line_id: r.prod_line_id ?? null,
    };
    setForm(selected);
    setInitialForm(selected);
    setHint("Loaded");
  }

  function resetForm() {
    setForm(initialForm);
    setHint("Reset");
  }

  const changed = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(initialForm),
    [form, initialForm]
  );

  async function onSave() {
    setErrorSave("");
    setHint("");

    if (!form.hd_exw_id) {
      setErrorSave("Select a config from the list first");
      return;
    }

    const payload = {
      hd_exw_id: form.hd_exw_id,
      exw: Number(form.exw),
    };

    if (!Number.isFinite(payload.exw)) {
      setErrorSave("EXW must be a number");
      return;
    }

    setSaving(true);
    try {
      const r = await fetch("/api/data?scope=srp_config", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const j = await r.json();
      if (!r.ok || !j.ok) throw new Error(j?.error || "HTTP " + r.status);

      // Update list + form with returned row
      setRows((prev) =>
        prev.map((row) => (row.hd_exw_id === j.row.hd_exw_id ? j.row : row))
      );
      selectRow(j.row);
      setHint("Saved changes");
    } catch (e) {
      setErrorSave(String(e.message || e));
    } finally {
      setSaving(false);
    }
  }

  function back() {
    nav("/purchase/config");
  }

  function onChange(e) {
    const { name, type, value } = e.target;
    let v = value;
    if (type === "number") v = value === "" ? "" : Number(value);
    setForm((f) => ({ ...f, [name]: v }));
  }

  // ---------- Simple layout (global classes; minimal inline) ----------
  const grid = {
    display: "grid",
    gridTemplateColumns: isNarrow ? "1fr" : "360px 1fr",
    gap: isNarrow ? 10 : 16,
    alignItems: "start",
  };

  const fieldGrid = {
    display: "grid",
    gridTemplateColumns: isNarrow ? "1fr" : "210px 1fr",
    gap: 10,
    alignItems: "center",
  };

  const smallText = { fontSize: isNarrow ? 11 : 12, opacity: 0.75 };
  const listItemText = isNarrow ? 15 : 14;

  return (
    <div className="app-page">
      <main className="container" style={isNarrow ? { fontSize: 15 } : undefined}>
        <h1 className="menu-title" style={isNarrow ? { fontSize: 22, lineHeight: "24px" } : undefined}>
          Tuiss Web Tools — Purchase
        </h1>
        <h2 style={{ marginTop: -8, ...(isNarrow ? { fontSize: 16 } : {}) }}>
          Retail Price Component Configuration
        </h2>

        {/* Top buttons */}
        <div style={{ margin: "8px 0 12px", display: "flex", flexWrap: "wrap", gap: 8 }}>
          <Button onClick={back}>← Trở về / Back</Button>
          {/* No New button (cannot add) */}
          <Button onClick={resetForm} disabled={!changed}>Reset</Button>
          <Button onClick={onSave} disabled={saving || !changed || !form.hd_exw_id}>
            {saving ? "Saving..." : "Save"}
          </Button>
          {hint ? <span style={{ marginLeft: 4, ...smallText }}>{hint}</span> : null}
        </div>

        <div style={grid}>
          {/* LEFT: manual search + results */}
          <section className="box" style={{ overflow: "hidden" }}>
            <label style={{ display: "block", fontWeight: 600, ...(isNarrow ? { fontSize: 14 } : {}) }}>
              Search / Chọn (Tên bộ phận)
            </label>
            <input
              ref={qRef}
              className="text-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="type to search..."
            />
            <div style={{ marginTop: 6, ...smallText }}>
              {loadingList ? "Loading..." : rows.length + " item(s)"}
              {errorList ? <span style={{ color: "crimson", marginLeft: 8 }}>{errorList}</span> : null}
            </div>

            <div
              style={{
                marginTop: 8,
                maxHeight: isNarrow ? 320 : 440,
                overflow: "auto",
                borderTop: "1px solid #eee",
              }}
            >
              {rows.map((r) => {
                const sel = r.hd_exw_id === form.hd_exw_id && form.hd_exw_id != null;
                return (
                  <button
                    key={r.hd_exw_id}
                    onClick={() => selectRow(r)}
                    className="menu-card"
                    style={{
                      width: "100%",
                      display: "block",
                      textAlign: "left",
                      padding: isNarrow ? "10px 10px" : "8px 10px",
                      border: "none",
                      borderBottom: "1px solid #f0f0f0",
                      borderRadius: 0,
                      background: sel ? "rgba(0,0,0,0.05)" : "transparent",
                      fontSize: listItemText,
                    }}
                    title="Select"
                  >
                    <div style={{ fontWeight: 600 }}>{r.opt_name || "(no name)"}</div>
                    <div style={{ marginTop: 2, ...smallText }}>
                      {"id #" + r.hd_exw_id} {" • value " + fmtNum(r.exw)}
                    </div>
                  </button>
                );
              })}
              {rows.length === 0 && !loadingList ? (
                <div style={{ padding: 8, ...smallText }}>No results</div>
              ) : null}
            </div>
          </section>

          {/* RIGHT: editable form */}
          <section className="box" style={{ marginTop: isNarrow ? 12 : 0 }}>
            <div style={fieldGrid}>
              <label>Bộ phận / Component</label>
              <input
                className="text-input"
                name="opt_name"
                value={form.opt_name}
                readOnly
                title="Name is read-only"
              />

              <label>Value</label>
              <input
                className="text-input"
                type="number"
                step="0.01"
                min="0"
                name="exw"
                value={form.exw}
                onChange={onChange}
                placeholder="0"
              />
            </div>

            {errorSave ? (
              <div style={{ color: "crimson", marginTop: 10 }}>{errorSave}</div>
            ) : (
              <div style={{ marginTop: 10, ...smallText }}>
                Group ID fixed at 5. Select an item on the left to edit its EXW.
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}

// ---------- helpers ----------
function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}
function fmtNum(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}
